import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Fingerprint, Smartphone, Laptop, Trash2, Plus, AlertCircle, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = window.__BACKEND_URL__ || 'http://localhost:8001';

const BiometricAuth = () => {
  const { token, user } = useAuth();
  const { toast } = useToast();
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [showEnrollDialog, setShowEnrollDialog] = useState(false);
  const [deviceName, setDeviceName] = useState('');
  const [isSupported, setIsSupported] = useState(false);

  useEffect(() => {
    // Check if WebAuthn is supported
    setIsSupported(
      window.PublicKeyCredential !== undefined &&
      navigator.credentials !== undefined
    );
    
    if (token) {
      fetchCredentials();
    }
  }, [token]);

  const fetchCredentials = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/webauthn/credentials`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCredentials(response.data.credentials || []);
    } catch (error) {
      console.error('Error fetching credentials:', error);
    } finally {
      setLoading(false);
    }
  };

  const base64urlToBuffer = (base64url) => {
    const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
    const padLength = (4 - (base64.length % 4)) % 4;
    const padded = base64 + '='.repeat(padLength);
    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  };

  const bufferToBase64url = (buffer) => {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  };

  const handleEnrollDevice = async () => {
    if (!deviceName.trim()) {
      toast({
        title: 'Device name required',
        description: 'Please enter a name for this device',
        variant: 'destructive'
      });
      return;
    }

    try {
      setEnrolling(true);

      // Step 1: Begin registration
      const beginResponse = await axios.post(
        `${BACKEND_URL}/api/webauthn/register/begin`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const options = beginResponse.data;

      // Convert base64 strings to ArrayBuffers
      const publicKeyCredentialCreationOptions = {
        challenge: base64urlToBuffer(options.challenge),
        rp: options.rp,
        user: {
          id: base64urlToBuffer(options.user.id),
          name: options.user.name,
          displayName: options.user.displayName
        },
        pubKeyCredParams: options.pubKeyCredParams,
        timeout: options.timeout,
        attestation: options.attestation,
        authenticatorSelection: {
          authenticatorAttachment: 'platform', // Use built-in biometrics
          requireResidentKey: false,
          userVerification: 'required'
        }
      };

      // Step 2: Create credential using browser API
      const credential = await navigator.credentials.create({
        publicKey: publicKeyCredentialCreationOptions
      });

      if (!credential) {
        throw new Error('Failed to create credential');
      }

      // Step 3: Complete registration
      const credentialData = {
        id: credential.id,
        rawId: bufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
          clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
          attestationObject: bufferToBase64url(credential.response.attestationObject)
        }
      };

      await axios.post(
        `${BACKEND_URL}/api/webauthn/register/complete`,
        {
          credential: credentialData,
          device_name: deviceName
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast({
        title: 'Device enrolled successfully!',
        description: `${deviceName} can now be used for biometric sign-in`,
      });

      setShowEnrollDialog(false);
      setDeviceName('');
      fetchCredentials();
    } catch (error) {
      console.error('Enrollment error:', error);
      let errorMessage = 'Failed to enroll device';
      
      if (error.name === 'NotAllowedError') {
        errorMessage = 'Permission denied. Please try again and allow biometric access.';
      } else if (error.name === 'NotSupportedError') {
        errorMessage = 'Your device does not support biometric authentication.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }

      toast({
        title: 'Enrollment failed',
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setEnrolling(false);
    }
  };

  const handleDeleteCredential = async (credentialId) => {
    if (!window.confirm('Are you sure you want to remove this device? You will need to re-enroll it to use biometric sign-in.')) {
      return;
    }

    try {
      await axios.delete(
        `${BACKEND_URL}/api/webauthn/credentials/${credentialId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast({
        title: 'Device removed',
        description: 'The device has been removed from your account',
      });

      fetchCredentials();
    } catch (error) {
      console.error('Error deleting credential:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove device',
        variant: 'destructive'
      });
    }
  };

  const getDeviceIcon = (deviceType) => {
    if (deviceType === 'platform') {
      return <Smartphone className="w-5 h-5 text-blue-600" />;
    }
    return <Laptop className="w-5 h-5 text-purple-600" />;
  };

  if (!isSupported) {
    return (
      <Card className="border-orange-200">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <AlertCircle className="w-6 h-6 text-orange-600" />
            <span>Biometric Authentication Not Available</span>
          </CardTitle>
          <CardDescription>
            Your browser or device does not support biometric authentication (Face ID, Touch ID, Fingerprint).
            Please use a modern browser on a device with biometric capabilities.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center space-x-2">
                <Fingerprint className="w-6 h-6 text-blue-600" />
                <span>Biometric Authentication</span>
              </CardTitle>
              <CardDescription className="mt-2">
                Use Face ID, Touch ID, or fingerprint to sign in securely without passwords
              </CardDescription>
            </div>
            <Button
              onClick={() => setShowEnrollDialog(true)}
              disabled={enrolling}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Device
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-500">
              Loading devices...
            </div>
          ) : credentials.length === 0 ? (
            <div className="text-center py-8">
              <Fingerprint className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-slate-400 mb-4">No devices enrolled yet</p>
              <p className="text-sm text-slate-500">
                Add your device to enable fast, secure biometric sign-in
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {credentials.map((cred) => (
                <div
                  key={cred.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-[#0F1923]"
                >
                  <div className="flex items-center space-x-4">
                    {getDeviceIcon(cred.device_type)}
                    <div>
                      <p className="font-medium text-white">
                        {cred.device_name || 'Unnamed Device'}
                      </p>
                      <p className="text-sm text-slate-500">
                        Added {new Date(cred.created_at).toLocaleDateString()}
                        {cred.last_used && (
                          <span> • Last used {new Date(cred.last_used).toLocaleDateString()}</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteCredential(cred.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start space-x-3">
              <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-blue-900 mb-1">Supported Devices:</p>
                <ul className="text-blue-700 space-y-1">
                  <li>• iPhone/iPad with Face ID or Touch ID</li>
                  <li>• Android devices with fingerprint sensor</li>
                  <li>• Mac with Touch ID</li>
                  <li>• Windows devices with Windows Hello</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Enrollment Dialog */}
      <Dialog open={showEnrollDialog} onOpenChange={setShowEnrollDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enroll Biometric Device</DialogTitle>
            <DialogDescription>
              Give your device a name to identify it later. You'll then be prompted to use your device's biometric sensor.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="deviceName">Device Name</Label>
            <Input
              id="deviceName"
              placeholder="e.g., My iPhone, Work Laptop"
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              className="mt-2"
              disabled={enrolling}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowEnrollDialog(false);
                setDeviceName('');
              }}
              disabled={enrolling}
            >
              Cancel
            </Button>
            <Button
              onClick={handleEnrollDevice}
              disabled={enrolling || !deviceName.trim()}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {enrolling ? (
                <>
                  <span className="mr-2">Enrolling...</span>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                </>
              ) : (
                <>
                  <Fingerprint className="w-4 h-4 mr-2" />
                  Enroll Device
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BiometricAuth;
